import { useEffect, type RefObject } from "react";
import { BASE_API_URL_V2 } from "@/constants";

/** The root-relative path the preview writes for each OneNote or Teams image. */
export const SIGNED_IMAGE_PATH_PREFIX = "/api/v2/connections/microsoft/image/";

const SIGNED_SRC = new RegExp(
  `(\\ssrc=")(${SIGNED_IMAGE_PATH_PREFIX.replace(/\//g, "\\/")})`,
  "g"
);

/**
 * Point each signed image path in `html` at the API origin. The preview writes
 * root-relative paths because only the browser knows that origin (a tenant
 * serves the app and the API from one), and the app is served from another.
 */
export const resolveSignedImageSources = (
  html: string,
  apiOrigin: string = BASE_API_URL_V2
): string => html.replace(SIGNED_SRC, `$1${apiOrigin}$2`);

const showFailedImage = (img: HTMLImageElement) => {
  const placeholder = document.createElement("span");
  placeholder.dataset.imageFailed = "true";
  placeholder.textContent = "Image could not load";
  placeholder.style.cssText =
    "color: #888; font-style: italic; padding: 8px; border: 1px dashed #ccc; display: inline-block; background-color: #f9f9f9;";
  img.replaceWith(placeholder);
};

const hasFailed = (img: HTMLImageElement) =>
  img.complete && img.naturalWidth === 0 && Boolean(img.getAttribute("src"));

/**
 * Replace each image under `container` that fails to load with a placeholder,
 * leaving the rest of the content and the other images as they are. `content`
 * is the HTML the container renders: a new value re-checks the new images.
 */
export const useFailedImagePlaceholders = (
  container: RefObject<HTMLElement | null>,
  content: string
) => {
  useEffect(() => {
    const root = container.current;
    if (!root) return;
    // `error` does not bubble, so it is caught on the way down.
    const onError = (event: Event) => {
      if (event.target instanceof HTMLImageElement) {
        showFailedImage(event.target);
      }
    };
    root.addEventListener("error", onError, true);
    // An image that failed before the listener was added.
    root.querySelectorAll("img").forEach((img) => {
      if (hasFailed(img)) showFailedImage(img);
    });
    return () => root.removeEventListener("error", onError, true);
  }, [container, content]);
};
